export type DiagnosticErrorDisplay = {
  description: string;
  type: string;
  name: string;
  operatorGuidance: string;
  supportGuidance: string;
};

const buildDiagnosticGuidance = (
  operatorGuidance: string,
  supportGuidance: string,
) => ({
  operatorGuidance: `Para o operador: ${operatorGuidance}`,
  supportGuidance: `Para o suporte: ${supportGuidance}`,
});

export const DIAGNOSTIC_ERROR_DISPLAY_MAP: Record<
  string,
  DiagnosticErrorDisplay
> = {
  E1: {
    name: 'Falha ao iniciar comunicação com câmera QrCode.',
    type: 'ERROR',
    description: 'Falha ao iniciar comunicação com câmera QrCode.',
    ...buildDiagnosticGuidance(
      'Verificar cabeamento da câmera QR Code e reiniciar o DBD; se persistir, acionar o suporte.',
      'Validar alimentação, conexão física, permissões e logs de inicialização da câmera QR Code.',
    ),
  },
  E2: {
    name: 'QRCode da câmera não foi configurado.',
    type: 'ERROR',
    description: 'QRCode da câmera não foi configurado.',
    ...buildDiagnosticGuidance(
      'Confirmar a configuração da câmera QR Code e acionar o suporte se o equipamento continuar sem leitura.',
      'Revisar provisionamento e parâmetros da câmera QR Code no DBD.',
    ),
  },
  E3: {
    name: 'Falha ao iniciar comunicação com leitor.',
    type: 'ERROR',
    description: 'Falha ao iniciar comunicação com leitor.',
    ...buildDiagnosticGuidance(
      'Testar a leitura novamente e verificar a conexão do leitor; se persistir, acionar o suporte.',
      'Validar comunicação serial ou USB do leitor, alimentação e logs de inicialização.',
    ),
  },
  E4: {
    name: 'leitor IDTech com data errada.',
    type: 'ERROR',
    description: 'leitor IDTech com data errada.',
    ...buildDiagnosticGuidance(
      'Verificar data e hora do equipamento e reiniciar o leitor; se persistir, acionar o suporte.',
      'Sincronizar o relógio do leitor IDTech, revisar timezone ou NTP e logs de configuração.',
    ),
  },
  E5: {
    name: 'Leitor IDTech não configurado.',
    type: 'ERROR',
    description: 'Leitor IDTech não configurado.',
    ...buildDiagnosticGuidance(
      'Confirmar se o leitor está instalado e configurado corretamente antes de liberar o equipamento.',
      'Revisar provisionamento do leitor IDTech e parâmetros enviados ao dispositivo.',
    ),
  },
  E6: {
    name: 'Falha ao iniciar comunicação com câmera facial.',
    type: 'ERROR',
    description: 'Falha ao iniciar comunicação com câmera facial.',
    ...buildDiagnosticGuidance(
      'Verificar a conexão da câmera facial e reiniciar o equipamento; se persistir, acionar o suporte.',
      'Validar alimentação, cabo, permissões e logs de inicialização da câmera facial.',
    ),
  },
  E7: {
    name: 'Falha ao capturar imagem corretamente.',
    type: 'ERROR',
    description: 'Falha ao capturar imagem corretamente.',
    ...buildDiagnosticGuidance(
      'Limpar ou ajustar o posicionamento da câmera e repetir a captura; se persistir, acionar o suporte.',
      'Analisar foco, iluminação, calibração e erros retornados pelo módulo de captura.',
    ),
  },
  E8: {
    name: 'Falha ao enviar dados da câmera para DBD.',
    type: 'ERROR',
    description: 'Falha ao enviar dados da câmera para DBD.',
    ...buildDiagnosticGuidance(
      'Tentar nova captura e verificar a estabilidade do equipamento; se persistir, acionar o suporte.',
      'Revisar o canal interno de envio entre câmera e DBD e logs do pipeline de imagem.',
    ),
  },
  E9: {
    name: 'Falha ao iniciar comunicação com ATMEGA.',
    type: 'ERROR',
    description: 'Falha ao iniciar comunicação com ATMEGA.',
    ...buildDiagnosticGuidance(
      'Reiniciar o DBD e verificar a conexão com o módulo; se persistir, acionar o suporte.',
      'Validar comunicação com o ATMEGA, firmware e alimentação do periférico.',
    ),
  },
  E10: {
    name: 'N/D (LED)',
    type: 'NEUTRAL',
    description: 'N/D (LED)',
    ...buildDiagnosticGuidance(
      'Sem ação operacional imediata; registrar recorrência se houver impacto visual no equipamento.',
      'Confirmar a telemetria do LED e revisar a instrumentação antes de tratar como falha física.',
    ),
  },
  E11: {
    name: 'Falha ao iniciar comunicação com o Buzzer.',
    type: 'ERROR',
    description: 'Falha ao iniciar comunicação com o Buzzer.',
    ...buildDiagnosticGuidance(
      'Verificar se o buzzer responde após reinício; se persistir, acionar o suporte.',
      'Analisar comunicação com o buzzer, chicote e logs de inicialização.',
    ),
  },
  E12: {
    name: 'Falha ao iniciar o módulo Buzzer.',
    type: 'ERROR',
    description: 'Falha ao iniciar o módulo Buzzer.',
    ...buildDiagnosticGuidance(
      'Reiniciar o equipamento e validar a emissão sonora; se persistir, acionar o suporte.',
      'Validar carga do módulo buzzer e possíveis falhas no firmware do periférico.',
    ),
  },
  E13: {
    name: 'Falha ao abrir rede wifi.',
    type: 'ERROR',
    description: 'Falha ao abrir rede wifi.',
    ...buildDiagnosticGuidance(
      'Verificar a disponibilidade da rede Wi-Fi configurada e reiniciar o equipamento se necessário.',
      'Revisar SSID, credenciais, interface de rede e logs de associação Wi-Fi.',
    ),
  },
  E14: {
    name: 'Falha ao ativar rede móvel.',
    type: 'WARNING',
    description: 'Falha ao ativar rede móvel.',
    ...buildDiagnosticGuidance(
      'Checar sinal e chip de dados e manter o equipamento monitorado; acionar o suporte se persistir.',
      'Validar modem, APN, cobertura da operadora e logs de conexão móvel.',
    ),
  },
  E15: {
    name: 'Falha ao iniciar comunicação com módulo GPS.',
    type: 'ERROR',
    description: 'Falha ao iniciar comunicação com módulo GPS.',
    ...buildDiagnosticGuidance(
      'Verificar antena GPS e posicionamento do equipamento; se persistir, acionar o suporte.',
      'Revisar comunicação do módulo GPS, antena, alimentação e logs de satélite.',
    ),
  },
  E16: {
    name: 'N/D (GPS)',
    type: 'NEUTRAL',
    description: 'N/D (GPS)',
    ...buildDiagnosticGuidance(
      'Sem ação imediata; acompanhar se o GPS volta a reportar normalmente.',
      'Confirmar a origem do status N/D e validar telemetria e configuração do GPS.',
    ),
  },
  E17: {
    name: 'Falha ao baixar dados hostSettings.',
    type: 'ERROR',
    description: 'Falha ao baixar dados hostSettings.',
    ...buildDiagnosticGuidance(
      'Manter o equipamento em observação e acionar o suporte se a configuração não for atualizada.',
      'Verificar download de hostSettings, conectividade e resposta do backend de configuração.',
    ),
  },
  E18: {
    name: 'Falha ao baixar dados hostSettings.',
    type: 'ERROR',
    description: 'Falha ao baixar dados hostSettings.',
    ...buildDiagnosticGuidance(
      'Aguardar nova sincronização e acionar o suporte se a configuração seguir desatualizada.',
      'Analisar a etapa de obtenção ou aplicação de hostSettings e logs do fluxo de sincronização.',
    ),
  },
  E19: {
    name: 'Falha ao configurar a data DBD do servidor.',
    type: 'ERROR',
    description: 'Falha ao configurar a data DBD do servidor.',
    ...buildDiagnosticGuidance(
      'Verificar a data e hora exibidas no equipamento e reiniciar o DBD se necessário.',
      'Validar sincronismo entre servidor e DBD, timezone e rotina de ajuste de relógio.',
    ),
  },
  E20: {
    name: 'Falha ao configurar a data DBD do leitor.',
    type: 'ERROR',
    description: 'Falha ao configurar a data DBD do leitor.',
    ...buildDiagnosticGuidance(
      'Repetir a sincronização do leitor e acionar o suporte se a data continuar divergente.',
      'Revisar a rotina de ajuste de data e hora no leitor e logs de comunicação com o DBD.',
    ),
  },
  E21: {
    name: 'Acesso múltiplo dentro de um limite de tempo (EMV)',
    type: 'ERROR',
    description: 'Acesso múltiplo dentro de um limite de tempo (EMV)',
    ...buildDiagnosticGuidance(
      'Orientar nova tentativa após o intervalo configurado e registrar recorrência se houver uso legítimo bloqueado.',
      'Validar a regra de anti-passback EMV, a janela configurada e o histórico do cartão tokenizado.',
    ),
  },
  E22: {
    name: 'Condições de bloqueio estabelecidas pelo emissor',
    type: 'ERROR',
    description: 'Condições de bloqueio estabelecidas pelo emissor',
    ...buildDiagnosticGuidance(
      'Informar o passageiro sobre bloqueio do emissor; não há ação local no DBD.',
      'Confirmar o retorno do emissor e investigar apenas se houver volume anormal por BIN ou bandeira.',
    ),
  },
  E23: {
    name: 'TAG obrigatória não identificada no cartão',
    type: 'ERROR',
    description: 'TAG obrigatória não identificada no cartão',
    ...buildDiagnosticGuidance(
      'Solicitar nova aproximação do cartão; se persistir com vários cartões, acionar o suporte.',
      'Revisar leitura EMV, tags obrigatórias e integridade da antena ou do leitor.',
    ),
  },
  E24: {
    name: 'EMV Mobile não aceito pelo dispositivo (seccionado)',
    type: 'ERROR',
    description: 'EMV Mobile não aceito pelo dispositivo (seccionado)',
    ...buildDiagnosticGuidance(
      'Informar que o pagamento EMV Mobile não está aceito neste equipamento ou operação.',
      'Validar a parametrização de aceitação de carteiras móveis no dispositivo e na adquirente.',
    ),
  },
  E30: {
    name: 'Falha ao obter o token do Pix na adquirente',
    type: 'ERROR',
    description: 'Falha ao obter o token do Pix na adquirente',
    ...buildDiagnosticGuidance(
      'Aguardar alguns minutos e tentar novamente; se persistir, acionar o suporte.',
      'Verificar credenciais, disponibilidade da adquirente e logs da autenticação Pix.',
    ),
  },
  E31: {
    name: 'Temperatura da CPU acima do valor normal.',
    type: 'WARNING',
    description: 'Temperatura da CPU acima do valor normal.',
    ...buildDiagnosticGuidance(
      'Verificar ventilação do equipamento e reduzir exposição a calor excessivo; acionar o suporte se persistir.',
      'Analisar telemetria térmica, ventilação, dissipação e carga de processamento do DBD.',
    ),
  },
  E32: {
    name: 'Uso de CPU acima do valor normal.',
    type: 'WARNING',
    description: 'Uso de CPU acima do valor normal.',
    ...buildDiagnosticGuidance(
      'Acompanhar lentidão do equipamento e reiniciar fora de operação se necessário.',
      'Investigar processos com alto consumo de CPU, gargalos e logs de performance.',
    ),
  },
  E33: {
    name: 'Falha de comunicação com o dispositivo Pix',
    type: 'ERROR',
    description: 'Falha de comunicação com o dispositivo Pix',
    ...buildDiagnosticGuidance(
      'Verificar conexões do dispositivo Pix e repetir a operação; acionar o suporte se persistir.',
      'Revisar protocolo de comunicação, firmware e handshake com o periférico Pix.',
    ),
  },
  E41: {
    name: 'Uso de memória acima do valor normal.',
    type: 'WARNING',
    description: 'Uso de memória acima do valor normal.',
    ...buildDiagnosticGuidance(
      'Evitar novas operações pesadas no equipamento e acionar o suporte se a lentidão persistir.',
      'Analisar consumo de memória, vazamentos, processos residentes e limites do sistema.',
    ),
  },
  E51: {
    name: 'Uso do disco acima do valor normal.',
    type: 'WARNING',
    description: 'Uso do disco acima do valor normal.',
    ...buildDiagnosticGuidance(
      'Verificar se o equipamento segue operando normalmente e acionar o suporte se o alerta continuar.',
      'Revisar ocupação de disco, arquivos temporários, logs e rotina de limpeza.',
    ),
  },
  E52: {
    name: 'Não é possível montar o disco externo do cartão SD.',
    type: 'ERROR',
    description: 'Não é possível montar o disco externo do cartão SD.',
    ...buildDiagnosticGuidance(
      'Não remover o cartão SD com o equipamento ligado e acionar o suporte.',
      'Validar montagem do cartão SD, integridade física, conector e sistema de arquivos.',
    ),
  },
  E53: {
    name: 'o cartão SD está corrompido. Iniciando a formatação do disco.',
    type: 'WARNING',
    description:
      'o cartão SD está corrompido. Iniciando a formatação do disco.',
    ...buildDiagnosticGuidance(
      'Manter o equipamento monitorado e evitar remover o cartão SD até avaliação do suporte.',
      'Analisar integridade do cartão SD, necessidade de backup ou troca e resultado da formatação.',
    ),
  },
  E60: {
    name: 'Timeouts consecutivos na catraca.',
    type: 'ERROR',
    description: 'Timeouts consecutivos na catraca.',
    ...buildDiagnosticGuidance(
      'Verificar travamento ou mau uso da catraca e acionar o suporte se os timeouts continuarem.',
      'Investigar comunicação com a catraca, sensores, tempo de resposta e logs de timeout.',
    ),
  },
  E70: {
    name:
      'Configurar nova viagem em relação à viagem atual é menor que o carimbo de data/hora da configuração',
    type: 'WARNING',
    description:
      'Configurar nova viagem em relação à viagem atual é menor que o carimbo de data/hora da configuração',
    ...buildDiagnosticGuidance(
      'Conferir horário e configuração da viagem antes de seguir operação.',
      'Revisar consistência entre timestamps de configuração e dados da viagem.',
    ),
  },
  E71: {
    name:
      'Recusando ação de fechamento porque o carimbo de data/hora de entrada é menor que o carimbo de data/hora de configuração',
    type: 'WARNING',
    description:
      'Recusando ação de fechamento porque o carimbo de data/hora de entrada é menor que o carimbo de data/hora de configuração',
    ...buildDiagnosticGuidance(
      'Conferir horário de entrada e configuração antes de tentar novo fechamento.',
      'Investigar discrepância de timestamps no fluxo de fechamento e sincronismo do relógio.',
    ),
  },
  E80: {
    name: 'Validador paralelo não conectado.',
    type: 'ERROR',
    description: 'Validador paralelo não conectado.',
    ...buildDiagnosticGuidance(
      'Confirmar a conexão do validador paralelo e acionar o suporte se permanecer desconectado.',
      'Validar cabeamento, alimentação, configuração e detecção do validador paralelo.',
    ),
  },
  E99: {
    name: 'Ocorreu falha de segmentação',
    type: 'ERROR',
    description: 'Ocorreu falha de segmentação',
    ...buildDiagnosticGuidance(
      'Retirar o equipamento de operação e acionar o suporte imediatamente.',
      'Coletar logs e crash dump, identificar a causa da falha de segmentação e avaliar rollback ou restart controlado.',
    ),
  },
};

export const SIGNAL_TYPE = [
  { key: 'mlan0', label: 'wi-fi' },
  { key: 'eth0', label: 'cabo' },
  { key: 'wwan0', label: 'móvel', signalQuality: true },
];
const CONST_DBD = {
  INTERNET_STATUSES: [
    { KEY: 0, VALUE: 'INDEFINIDO', color: 'grey' },
    { KEY: 1, VALUE: 'OPERACIONAL', color: '#36BD3B' },
    { KEY: 2, VALUE: 'PENDENTE', color: '#EEB759' },
    { KEY: 3, VALUE: 'NÃO OPERACIONAL', color: '#A62A2A' },
  ],
  CONECTIVITY_STATUSES: [
    { KEY: 0, VALUE: 'Pendente', color: '#EEB759' },
    { KEY: 1, VALUE: 'Online', color: '#009317' },
    { KEY: 2, VALUE: 'Offline', color: '#D10000' },
    { KEY: 3, VALUE: 'Sem Conexão', color: '#AAB8C2' },
  ],

  OPERATINOL_STATUS: [
    { KEY: 'configured', VALUE: 'Em Operação' },
    { KEY: 'installed', VALUE: 'Instalado' },
    { KEY: 'uninstalled', VALUE: 'Aguardando Instalação' },
  ],

  GPS_STATUSES: [
    { KEY: 0, VALUE: 'INDEFINIDO', color: 'grey' },
    { KEY: 1, VALUE: 'Ativo', color: '#36BD3B' },
    { KEY: 2, VALUE: 'SINAL INVÁLIDO', color: '#A62A2A' },
    { KEY: 3, VALUE: 'SEM COMUNICAÇÃO', color: '#828282' },
  ],
  INSTALLATION_STATUSES: [
    { KEY: 0, VALUE: 'DISPONÍVEL' },
    { KEY: 1, VALUE: 'INSTALADO' },
    { KEY: 2, VALUE: 'CONFIGURADO' },
  ],
  STATUS_DATA_MAP: new Map([
    ['applicationVersion', 'Versão do aplicativo'],
    ['dbdId', 'DBD'],
    ['dbdIp', 'Endereço IP'],
    ['dbdState', 'Estado do DBD'],
    ['diskUsage', 'Uso de disco'],
    ['gpsStatus', 'Status do GPS'],
    ['internetStatus', 'Status de conexão'],
    ['sdReadOnly', 'Cartão apenas leitura'],
    ['noSDCard', 'Sem cartão SD'],
    ['sdProblem', 'Cartão SD com problema'],
    ['settingsVersion', 'Versão de configurações'],
    ['temperature', 'Temperatura'],
    ['timestamp', 'Atualizado em'],
    ['uptime', 'Tempo em execução'],
    ['validationNumber', 'Número de validação'],
  ]),

  WAY: [
    { KEY: 0, VALUE: 'Entrada' },
    { KEY: 1, VALUE: 'Saída' },
  ],
  TYPE: [
    { KEY: 0, VALUE: 'Primário' },
    { KEY: 1, VALUE: 'Secundário' },
  ],
};

export default CONST_DBD;

const CONST_DBD_OBJECT = {
  INTERNET_STATUS: {
    UNDEFINED: { KEY: 0, VALUE: 'INDEFINIDO' },
    OK: { KEY: 1, VALUE: 'OPERACIONAL' },
    DELAYED: { KEY: 2, VALUE: 'PENDENTE' },
    NOK: { KEY: 3, VALUE: 'NÃO OPERACIONAL' },
  },
  GPS_STATUS: {
    UNDEFINED: { KEY: 0, VALUE: 'INDEFINIDO' },
    OK: { KEY: 1, VALUE: 'OPERACIONAL' },
    DELAYED: { KEY: 2, VALUE: 'PENDENTE' },
    NOK: { KEY: 3, VALUE: 'NÃO OPERACIONAL' },
  },
  INSTALLATION_STATUSES: {
    AVAILABLE: { KEY: 0, VALUE: 'DISPONÍVEL' },
    INSTALLED: { KEY: 1, VALUE: 'INSTALADO' },
    CONFIGURED: { KEY: 2, VALUE: 'CONFIGURADO' },
  },
  WAY: {
    ENTER: { KEY: 0, VALUE: 'Entrada' },
    EXIT: { KEY: 1, VALUE: 'Saída' },
  },
  TYPE: {
    PRIMARY: { KEY: 0, VALUE: 'Primário' },
    SECONDARY: { KEY: 1, VALUE: 'Secundário' },
  },
};
const VERSION = [
  { KEY: 0, VALUE: 'Requisitado' },
  { KEY: 1, VALUE: 'Download' },
  { KEY: 2, VALUE: 'Atualizado' },
];

export { CONST_DBD_OBJECT, VERSION };
